import { useContext, createContext, ReactNode } from "react";
import { useRouter } from "next/router";
import useLangstonContentGet from "@/content/useLangstonContentGet";
import { LangstonContentResponse } from "@/types/models/Content.interface";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery/useRestQuery";
import ResponseError from "@/errors/ResponseError";

type ViewPageContentContextType = {
  content: LangstonContentResponse | null | undefined;
  contentRequestError: ResponseError | undefined;
  isContentLoading: boolean;
};

const ViewPageContentContext = createContext<
  ViewPageContentContextType | undefined
>(undefined);

export const ViewPageContentContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  // Router
  const router = useRouter();
  const { query } = router;
  const { contentId } = query as {
    contentId: string;
  };

  // Fetching content/story data
  const {
    data: content,
    error,
    loading: isContentLoading,
  }: UseRestQueryResponse<LangstonContentResponse> = useLangstonContentGet<LangstonContentResponse>(
    contentId,
  );

  // Ensure error is an instance of ResponseError or undefined
  const contentRequestError: ResponseError | undefined =
    error instanceof ResponseError ? error : undefined;

  return (
    <ViewPageContentContext.Provider
      value={{
        content,
        contentRequestError,
        isContentLoading,
      }}
    >
      {children}
    </ViewPageContentContext.Provider>
  );
};

const useViewPageContentContext = () => {
  const viewPageContentContext = useContext(ViewPageContentContext);

  if (viewPageContentContext === undefined) {
    throw new Error(
      "ViewPageContentContext must be used within a ViewPageContentContextProvider.",
    );
  }

  return viewPageContentContext;
};

export default useViewPageContentContext;
